import { clearGlobalContexts, trackPageView } from '@snowplow/browser-tracker';
import cloneDeep from 'lodash/cloneDeep';
import {
  createContext,
  type ReactElement,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';

import { useAuth } from 'contexts/auth';
import { useFiltersFromURL } from 'hooks/alerts/use-filters-from-url';

import { debugSnowplow, getUserDomain } from './analytics.utils';
import {
  DEBUG_ANALYTICS,
  IGNORED_SNOWPLOW_PATHS,
  TRACKER_IDS,
} from './constants';
import useLogEvent from './hooks/useLogEvent';
import useTrackerInit from './hooks/useTrackerInit';

import type {
  StateType,
  AnalyticsParams,
  AnalyticsContextType,
} from './AnalyticsProvider.types';

const TrackingContext = createContext<AnalyticsContextType>({
  logEvent: () => null,
  analytics: {
    providers: {},
  },
});

function AnalyticsProvider({ children }: { children: ReactElement }) {
  const { user, company, isCurator } = useAuth();
  const { filtersFromURL } = useFiltersFromURL();
  const location = useLocation();
  const analyticsParams = useMemo<AnalyticsParams | undefined>(() => {
    const userId = user?.id.toString();
    const companyId = company?.id.toString();
    const companyName = company?.name;
    if (userId && companyId && companyName) {
      const { analytics: instanceData = {} } = company?.companyData;

      return {
        companyId,
        companyName,
        debug: DEBUG_ANALYTICS,
        providers: cloneDeep(instanceData),
        userId,
        emailDomain: getUserDomain(user?.email),
        isCurator,
        isV2: true,
      };
    }
  }, [
    company?.companyData,
    company?.id,
    company?.name,
    isCurator,
    user?.email,
    user?.id,
  ]);
  const [analytics, setAnalytics] = useState<StateType>({
    providers: {
      sp: {
        isInitialized: false,
        trackers: [],
      },
    },
  });

  useTrackerInit({ analytics, analyticsParams, setAnalytics });

  useEffect(() => {
    return () => clearGlobalContexts();
  }, []);

  useEffect(() => {
    const path = `${location.pathname}${location.search}`;
    if (
      analytics.providers?.sp?.isInitialized &&
      !IGNORED_SNOWPLOW_PATHS.test(path)
    ) {
      trackPageView({}, [TRACKER_IDS.sp.default]);

      analyticsParams?.debug &&
        debugSnowplow('called `trackPageView` with\n%o', { data: { path } });
    }
  }, [
    analytics.providers,
    analyticsParams?.debug,
    location.pathname,
    location.search,
  ]);

  const logEvent = useLogEvent({
    analytics,
    debug: analyticsParams?.debug,
    filtersFromURL,
  });

  const contextValue = useMemo(
    () => ({
      logEvent,
      analytics,
    }),
    [logEvent, analytics],
  );

  return (
    <TrackingContext.Provider value={contextValue}>
      {children}
    </TrackingContext.Provider>
  );
}

const useAnalytics = () => useContext(TrackingContext);

export { AnalyticsProvider, useAnalytics };
